import { useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Modale,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { PiedFormulaire } from './ReferentielsPages';
import { BandeauAnneeFigee } from '../components/SelecteurAnnee';
import { useAnnee } from '../hooks/useAnnee';
import { enCentimes, enEuros, formaterMontant } from '../lib/format';
import type { TypeActivite, TypeReduction } from '../types';

/**
 * Types de reduction (2026-10-12, lot 2 de la roadmap) : le referentiel
 * qui nomme les reductions d'une inscription. Par ecole et par annee,
 * comme la grille tarifaire ; le montant est FIGE sur chaque inscription
 * a la saisie, le changer ici ne touche donc rien de deja saisi.
 */
export function TypesReductionPage() {
  const { peut } = useAuth();
  const [inclureInactifs, setInclureInactifs] = useState(false);
  const [modale, setModale] = useState<{ ouverte: boolean; type: TypeReduction | null }>({
    ouverte: false,
    type: null,
  });

  const annee = useAnnee();
  const peutGerer = peut('type_reduction.gerer') && annee.modifiable;

  const types = useRessource(
    () =>
      annee.anneeId
        ? api.get<TypeReduction[]>('/api/types-reduction', { inclureInactifs, anneeId: annee.anneeId })
        : Promise.resolve([]),
    [inclureInactifs, annee.anneeId],
  );

  // L'ORDRE SE POSE EN GLISSANT LES LIGNES (2026-10-12). L'ordre local
  // s'applique aussitot, pour que la ligne reste la ou on l'a deposee le
  // temps que le serveur reponde ; les identifiants qu'il ne connait pas
  // (un type cree ensuite) vont a la suite.
  const [ordreLocal, setOrdreLocal] = useState<string[] | null>(null);
  const lignes = (() => {
    const donnees = types.donnees ?? [];
    if (!ordreLocal) return donnees;
    const rang = new Map(ordreLocal.map((id, index) => [id, index]));
    return [...donnees].sort(
      (a, b) => (rang.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rang.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
  })();

  const reordonner = async (ids: string[]) => {
    setOrdreLocal(ids);
    try {
      await api.put('/api/types-reduction/ordre', { anneeId: annee.anneeId, ids });
      types.recharger();
    } catch (cause) {
      setOrdreLocal(null);
      window.alert(cause instanceof ErreurApi ? cause.message : 'Réordonnancement impossible.');
    }
  };

  const basculer = async (type: TypeReduction) => {
    try {
      await api.patch(`/api/types-reduction/${type.id}/activation`, { actif: !type.actif });
      types.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Opération impossible.');
    }
  };

  const colonnes: Colonne<TypeReduction>[] = [
    {
      cle: 'libelle',
      entete: 'Type de réduction',
      rendu: (t) => <span className="font-medium">{t.libelle}</span>,
    },
    {
      cle: 'montant',
      entete: 'Montant',
      classe: 'text-right',
      rendu: (t) =>
        t.montant === null ? (
          <Badge ton="contour">libre</Badge>
        ) : (
          <span className="tabulaire font-medium">{formaterMontant(t.montant)}</span>
        ),
    },
    {
      cle: 'aide',
      entete: 'Critère',
      rendu: (t) => <span className="text-xs text-encre-500">{t.aide ?? '—'}</span>,
    },
    {
      cle: 'activites',
      entete: 'Types d’activité',
      // La portee : toute inscription, ou les seuls types d'activite listes.
      rendu: (t) =>
        t.toutes_activites ? (
          <span className="text-xs text-encre-500">Tous</span>
        ) : t.types_activite.length === 0 ? (
          <Badge ton="alerte">aucun</Badge>
        ) : (
          <span className="text-xs text-encre-700">
            {t.types_activite.map((x) => x.libelle).join(', ')}
          </span>
        ),
    },
    {
      cle: 'inscriptions',
      entete: 'Inscriptions',
      classe: 'text-right',
      rendu: (t) => <span className="tabulaire">{t.nb_inscriptions}</span>,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (t) => (
        <Badge ton={t.actif ? 'succes' : 'neutre'}>{t.actif ? 'Actif' : 'Désactivé'}</Badge>
      ),
    },
    {
      cle: 'actions',
      entete: '',
      classe: 'text-right',
      rendu: (t) =>
        peutGerer ? (
          <button
            type="button"
            className="text-sm text-encre-500 underline hover:text-encre-900"
            onClick={(evenement) => {
              evenement.stopPropagation();
              void basculer(t);
            }}
          >
            {t.actif ? 'Désactiver' : 'Réactiver'}
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <EnTetePage
        titre="Types de réduction"
        description="Les réductions qu’une inscription peut porter, avec leur montant. Elles se cumulent ; le montant est figé sur l’inscription à la saisie, le modifier ici ne change rien de déjà saisi. Une remise libre se chiffre et se motive à l’inscription. L’ordre de cette liste est celui de la saisie : glissez les lignes pour le changer."
        actions={
          peutGerer ? (
            <button
              type="button"
              className="bouton-principal"
              onClick={() => setModale({ ouverte: true, type: null })}
            >
              Nouveau type
            </button>
          ) : null
        }
      />

      {annee.annee && !annee.modifiable && <BandeauAnneeFigee annee={annee.annee} />}

      <label className="mb-4 inline-flex items-center gap-2 text-sm text-encre-600">
        <input
          type="checkbox"
          checked={inclureInactifs}
          onChange={(evenement) => setInclureInactifs(evenement.target.checked)}
        />
        Afficher les types désactivés
      </label>

      {types.erreur && <Erreur message={types.erreur} onReessayer={types.recharger} />}
      {types.chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={lignes}
            cleDe={(t) => t.id}
            messageVide="Aucun type de réduction pour cette année."
            onLigneCliquee={peutGerer ? (t) => setModale({ ouverte: true, type: t }) : undefined}
            onReordonner={peutGerer ? (ids) => void reordonner(ids) : undefined}
          />
        </div>
      )}

      <FormulaireTypeReduction
        etat={modale}
        anneeId={annee.anneeId}
        onFermer={() => setModale({ ouverte: false, type: null })}
        onEnregistre={() => {
          setModale({ ouverte: false, type: null });
          types.recharger();
        }}
      />
    </>
  );
}

function FormulaireTypeReduction({
  etat,
  anneeId,
  onFermer,
  onEnregistre,
}: {
  etat: { ouverte: boolean; type: TypeReduction | null };
  anneeId: string;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    libelle: '',
    libre: false,
    montant: '',
    aide: '',
    toutes: true,
    typeActiviteIds: [] as string[],
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  // Les types d'activite de l'annee, pour restreindre la portee. Charges
  // modale ouverte seulement.
  const typesActivite = useRessource(
    () =>
      etat.ouverte
        ? api.get<TypeActivite[]>('/api/types-activite', { anneeId, inclureInactifs: true })
        : Promise.resolve([]),
    [etat.ouverte, anneeId],
  );

  const cle = etat.ouverte ? (etat.type?.id ?? 'nouveau') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setValeurs({
      libelle: etat.type?.libelle ?? '',
      libre: etat.type ? etat.type.montant === null : false,
      montant: etat.type?.montant != null ? enEuros(etat.type.montant) : '',
      aide: etat.type?.aide ?? '',
      toutes: etat.type?.toutes_activites ?? true,
      typeActiviteIds: etat.type?.types_activite.map((x) => x.id) ?? [],
    });
    setErreur(null);
  }
  if (!etat.ouverte && initialisePour !== null) setInitialisePour(null);

  const basculerTypeActivite = (id: string) => {
    setValeurs({
      ...valeurs,
      typeActiviteIds: valeurs.typeActiviteIds.includes(id)
        ? valeurs.typeActiviteIds.filter((x) => x !== id)
        : [...valeurs.typeActiviteIds, id],
    });
  };

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);

    const montant = valeurs.libre ? null : enCentimes(valeurs.montant);
    if (montant === undefined || (!valeurs.libre && montant === null)) {
      setErreur('Montant invalide : utilisez le format 20,00.');
      return;
    }

    if (!valeurs.toutes && valeurs.typeActiviteIds.length === 0) {
      setErreur('Cochez au moins un type d’activité, ou « Toutes les activités ».');
      return;
    }

    setEnCours(true);
    try {
      const corps = {
        libelle: valeurs.libelle,
        montant,
        aide: valeurs.aide || null,
        toutesActivites: valeurs.toutes,
        typeActiviteIds: valeurs.toutes ? [] : valeurs.typeActiviteIds,
      };
      if (etat.type) {
        await api.patch(`/api/types-reduction/${etat.type.id}`, corps);
      } else {
        await api.post('/api/types-reduction', { ...corps, anneeId });
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={etat.type ? `Modifier ${etat.type.libelle}` : 'Nouveau type de réduction'}
      ouverte={etat.ouverte}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        <Champ libelle="Libellé">
          <input
            className="champ"
            value={valeurs.libelle}
            onChange={(e) => setValeurs({ ...valeurs, libelle: e.target.value })}
            required
            autoFocus
          />
        </Champ>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={valeurs.libre}
            onChange={(e) => setValeurs({ ...valeurs, libre: e.target.checked })}
          />
          <span>
            Remise libre
            <span className="mt-0.5 block text-xs text-encre-400">
              Le montant se saisit à l’inscription, avec un motif obligatoire.
            </span>
          </span>
        </label>

        {!valeurs.libre && (
          <Champ
            libelle="Montant (€)"
            aide={
              (etat.type?.nb_inscriptions ?? 0) > 0
                ? `${etat.type!.nb_inscriptions} inscription(s) portent ce type : elles gardent le montant figé à leur saisie.`
                : 'Figé sur chaque inscription au moment de la saisie.'
            }
          >
            <input
              className="champ tabulaire"
              value={valeurs.montant}
              onChange={(e) => setValeurs({ ...valeurs, montant: e.target.value })}
              inputMode="decimal"
              required
            />
          </Champ>
        )}

        <Champ
          libelle="Critère"
          aide="Rappelé à la saisie d’une inscription — « moins de 26 ans ou plus de 65 ans ». Il ne décide rien."
        >
          <input
            className="champ"
            value={valeurs.aide}
            onChange={(e) => setValeurs({ ...valeurs, aide: e.target.value })}
          />
        </Champ>

        {/* LA PORTEE : toute inscription, ou les seuls types d'activite
            coches — la reduction chorale ne se propose pas sur un piano. */}
        <fieldset className="space-y-2 rounded-md border border-encre-200 p-3">
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-encre-500">
            S’applique à
          </legend>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={valeurs.toutes}
              onChange={(e) => setValeurs({ ...valeurs, toutes: e.target.checked })}
            />
            <span>
              Toutes les activités
              <span className="mt-0.5 block text-xs text-encre-400">
                Décochez pour limiter la réduction aux inscriptions de certains types d’activité.
              </span>
            </span>
          </label>
          {!valeurs.toutes && (
            <ul className="ml-6 grid grid-cols-2 gap-x-3 gap-y-1">
              {(typesActivite.donnees ?? [])
                .filter((ta) => ta.actif || valeurs.typeActiviteIds.includes(ta.id))
                .map((ta) => (
                  <li key={ta.id}>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={valeurs.typeActiviteIds.includes(ta.id)}
                        onChange={() => basculerTypeActivite(ta.id)}
                      />
                      <span>
                        {ta.libelle}
                        {!ta.actif && <span className="ml-1 text-xs text-encre-400">(désactivé)</span>}
                      </span>
                    </label>
                  </li>
                ))}
            </ul>
          )}
        </fieldset>

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} />
      </form>
    </Modale>
  );
}
